import React, { useEffect, useRef } from "react";
import {
  FaCode,
  FaDatabase,
  FaGithub,
  FaGitkraken,
  FaJava,
  FaJs,
  FaLinux,
  FaNodeJs,
  FaShieldAlt,
  FaWhatsapp
} from "react-icons/fa";
import { Bubble, CoreNode, OrbitArea } from "./AboutMe.styles";

const ICONS: { key: string; node: React.ReactNode }[] = [
  { key: "java", node: <FaJava title="Java" size={28} /> },
  { key: "js", node: <FaJs title="JavaScript/TS" size={28} /> },
  { key: "security", node: <FaShieldAlt title="Segurança" size={28} /> },
  { key: "db", node: <FaDatabase title="Banco de Dados" size={28} /> },
  { key: "github", node: <FaGithub title="GitHub" size={28} /> },
  { key: "whatsapp", node: <FaWhatsapp title="WhatsApp" size={28} /> },
  { key: "gitkraken", node: <FaGitkraken title="GitKraken" size={28} /> },
  { key: "linux", node: <FaLinux title="Linux" size={28} /> },
  { key: "node", node: <FaNodeJs title="Node/Nest" size={28} /> },
  { key: "sql", node: <FaDatabase title="SQL" size={28} /> }
];

const BUBBLE_DIAMETER = 56;
const BUBBLE_RADIUS = BUBBLE_DIAMETER / 2;
const CORE_RADIUS = 46;
const MIN_SPEED = 0.35;
const MAX_SPEED = 2.1;

interface Ball {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

const randomBalls = (width: number, height: number): Ball[] => {
  const cx = width / 2;
  const cy = height / 2;

  return ICONS.map(() => {
    let x = 0;
    let y = 0;
    let tries = 0;
    do {
      x = BUBBLE_RADIUS + Math.random() * (width - BUBBLE_DIAMETER);
      y = BUBBLE_RADIUS + Math.random() * (height - BUBBLE_DIAMETER);
      tries += 1;
    } while (
      Math.hypot(x - cx, y - cy) < CORE_RADIUS + BUBBLE_RADIUS + 12 &&
      tries < 60
    );

    const angle = Math.random() * Math.PI * 2;
    const speed = MIN_SPEED + Math.random() * (MAX_SPEED - MIN_SPEED);
    return {
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed
    };
  });
};

/**
 * Arena física: os ícones flutuam em direções aleatórias e colidem entre si,
 * com o núcleo central e com as bordas da área — sem rotação uniforme.
 */
export const TechOrbit: React.FC = () => {
  const areaRef = useRef<HTMLDivElement>(null);
  const bubbleRefs = useRef<(HTMLDivElement | null)[]>([]);
  const ballsRef = useRef<Ball[]>([]);
  const dimsRef = useRef({ width: 0, height: 0 });
  const rafRef = useRef<number>();

  useEffect(() => {
    const area = areaRef.current;
    if (!area) return;

    const { width, height } = area.getBoundingClientRect();
    dimsRef.current = { width, height };
    ballsRef.current = randomBalls(width, height);

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width: w, height: h } = entry.contentRect;
      if (w <= 0 || h <= 0) return;
      dimsRef.current = { width: w, height: h };
      for (const ball of ballsRef.current) {
        ball.x = Math.min(Math.max(ball.x, BUBBLE_RADIUS), w - BUBBLE_RADIUS);
        ball.y = Math.min(Math.max(ball.y, BUBBLE_RADIUS), h - BUBBLE_RADIUS);
      }
    });
    resizeObserver.observe(area);

    const step = () => {
      const { width: w, height: h } = dimsRef.current;
      const cx = w / 2;
      const cy = h / 2;
      const balls = ballsRef.current;

      for (const b of balls) {
        b.x += b.vx;
        b.y += b.vy;

        if (b.x - BUBBLE_RADIUS < 0) {
          b.x = BUBBLE_RADIUS;
          b.vx = Math.abs(b.vx);
        } else if (b.x + BUBBLE_RADIUS > w) {
          b.x = w - BUBBLE_RADIUS;
          b.vx = -Math.abs(b.vx);
        }

        if (b.y - BUBBLE_RADIUS < 0) {
          b.y = BUBBLE_RADIUS;
          b.vy = Math.abs(b.vy);
        } else if (b.y + BUBBLE_RADIUS > h) {
          b.y = h - BUBBLE_RADIUS;
          b.vy = -Math.abs(b.vy);
        }

        const dx = b.x - cx;
        const dy = b.y - cy;
        const dist = Math.hypot(dx, dy);
        const minDist = BUBBLE_RADIUS + CORE_RADIUS;
        if (dist > 0 && dist < minDist) {
          const nx = dx / dist;
          const ny = dy / dist;
          b.x = cx + nx * minDist;
          b.y = cy + ny * minDist;
          const dot = b.vx * nx + b.vy * ny;
          b.vx -= 2 * dot * nx;
          b.vy -= 2 * dot * ny;
        }
      }

      // colisões entre os ícones (elástica, massas iguais)
      for (let i = 0; i < balls.length; i++) {
        for (let j = i + 1; j < balls.length; j++) {
          const a = balls[i];
          const b = balls[j];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const dist = Math.hypot(dx, dy);
          const minDist = BUBBLE_DIAMETER;
          if (dist > 0 && dist < minDist) {
            const nx = dx / dist;
            const ny = dy / dist;
            const overlap = (minDist - dist) / 2;
            a.x -= nx * overlap;
            a.y -= ny * overlap;
            b.x += nx * overlap;
            b.y += ny * overlap;

            const relVx = b.vx - a.vx;
            const relVy = b.vy - a.vy;
            const relDot = relVx * nx + relVy * ny;
            if (relDot < 0) {
              a.vx += relDot * nx;
              a.vy += relDot * ny;
              b.vx -= relDot * nx;
              b.vy -= relDot * ny;
            }
          }
        }
      }

      // pequenos impulsos aleatórios para manter o movimento caótico
      if (Math.random() < 0.015) {
        const ball = balls[Math.floor(Math.random() * balls.length)];
        ball.vx += (Math.random() - 0.5) * 0.4;
        ball.vy += (Math.random() - 0.5) * 0.4;
      }

      for (const b of balls) {
        const speed = Math.hypot(b.vx, b.vy);
        if (speed === 0) {
          const angle = Math.random() * Math.PI * 2;
          b.vx = Math.cos(angle) * MIN_SPEED;
          b.vy = Math.sin(angle) * MIN_SPEED;
        } else if (speed > MAX_SPEED) {
          b.vx = (b.vx / speed) * MAX_SPEED;
          b.vy = (b.vy / speed) * MAX_SPEED;
        } else if (speed < MIN_SPEED) {
          b.vx = (b.vx / speed) * MIN_SPEED;
          b.vy = (b.vy / speed) * MIN_SPEED;
        }
      }

      balls.forEach((b, i) => {
        const el = bubbleRefs.current[i];
        if (el) {
          el.style.transform = `translate(${b.x - BUBBLE_RADIUS}px, ${
            b.y - BUBBLE_RADIUS
          }px)`;
        }
      });

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <OrbitArea ref={areaRef}>
      <CoreNode>
        <FaCode size={30} color="#0b1535" />
      </CoreNode>
      {ICONS.map((def, i) => (
        <Bubble key={def.key} ref={(el) => (bubbleRefs.current[i] = el)}>
          {def.node}
        </Bubble>
      ))}
    </OrbitArea>
  );
};
